type FetchPlansHandler = (
  settings: Record<string, unknown>,
  currentSettings: Record<string, unknown>,
  currentConfigurationSettings: Record<string, unknown>,
) => () => Promise<Record<string, unknown>>;

type FetchWatchManageHandler = () => Promise<Record<string, unknown>>;

const PLAN = {
  id: 'SKU-A',
  details: [
    {
      name: 'Watch Plan A',
      description: 'One-Number Cellular Plan',
      description_extended: '$5 a month on top of your subscription fee',
      locale: 'en-US',
    },
  ],
} as const;

export const fetchCarrierPlansHandler: FetchPlansHandler =
  (settings, currentSettings, currentConfigurationSettings) => () =>
    Promise.resolve({
      ...settings,
      ...currentSettings,
      ...currentConfigurationSettings,
      plans: [PLAN],
    });

export const fetchWatchTransferPlanHandler: FetchPlansHandler =
  (settings, currentSettings, currentConfigurationSettings) => () =>
    Promise.resolve({
      ...settings,
      ...currentSettings,
      ...currentConfigurationSettings,
      plan: PLAN,
      source_watch_name: 'John Source Watch',
      target_watch_name: 'Doe Target Watch',
      // Echo the transfer subscription type so the preview data path matches
      // runtime; 'linked' previews the sa-watch copy when Family Plan is enabled.
      subscription_type: currentConfigurationSettings?.supports_family_plan
        ? 'linked'
        : 'paired',
    });

export const fetchWatchManageHandler: FetchWatchManageHandler = () =>
  Promise.resolve({
    requires_tc: true,
    devices: [
      {
        iccid: '8943174000000141708',
        activation_status: 'active',
        plan_offer_id: 'SKU-A',
        actions: ['suspend', 'cancel'],
      },
      {
        iccid: '8943174000000141709',
        activation_status: 'suspended',
        plan_offer_id: 'SKU-B',
        actions: ['reactivate', 'cancel'],
      },
    ],
    plans: [
      {
        id: 'SKU-A',
        details: [
          {
            name: 'Watch Plan A',
            description: 'One-Number Cellular Plan',
            description_extended: '$5 a month on top of your subscription fee',
            locale: 'en-US',
          },
        ],
      },
      {
        id: 'SKU-B',
        details: [
          {
            name: 'Watch Plan B',
            description: '2GB Watch Data Plan',
            description_extended: '$10 a month on top of your subscription fee',
            locale: 'en-US',
          },
        ],
      },
      {
        id: 'SKU-C',
        details: [
          {
            name: 'Watch Plan C',
            description: '5GB Watch Data Plan',
            description_extended: '$15 a month on top of your subscription fee',
            locale: 'en-US',
          },
        ],
      },
    ],
  });

type ProxyFunction = {
  fetchCarrierPlans?: () => Promise<Record<string, unknown>>;
  fetchWatchManage?: () => Promise<Record<string, unknown>>;
  [key: string]: () => Promise<unknown>;
};

const handleUndefinedFunction = () => (): Promise<null> =>
  Promise.resolve(null);

export const getServiceProxyFunction = (
  settings: Record<string, unknown>,
  currentSettings: Record<string, unknown>,
  currentConfigurationSettings: Record<string, unknown>,
): ProxyFunction =>
  new Proxy<ProxyFunction>({} as ProxyFunction, {
    get(target, prop: string | symbol) {
      // React 19 dev tooling (props diff in the performance track, DevTools)
      // introspects props with Object.prototype.toString / String(), which
      // hits this trap with Symbol keys (Symbol.toStringTag, Symbol.toPrimitive)
      // and with `toString` / `valueOf`. Stringifying a Symbol throws and
      // wedges the React root; answering `toString` with the stub returns a
      // Promise and throws on primitive conversion. Hand both straight back to
      // the target so only real service names reach the switch below.
      if (typeof prop === 'symbol' || prop in target) {
        return Reflect.get(target, prop);
      }

      const handleFetchWatchManage = (): Promise<Record<string, unknown>> => {
        console.log(`Calling function ${prop}`);
        return fetchWatchManageHandler();
      };

      const handleFetchCarrierPlans = (): Promise<Record<string, unknown>> => {
        console.log(`Calling function ${prop}`);
        return fetchCarrierPlansHandler(
          settings,
          currentSettings,
          currentConfigurationSettings,
        )();
      };

      const handleFetchWatchTransfer = (): Promise<Record<string, unknown>> => {
        console.log(`Calling function ${prop}`);

        return fetchWatchTransferPlanHandler(
          settings,
          currentSettings,
          currentConfigurationSettings,
        )();
      };

      switch (prop) {
        case 'fetchWatchManage':
          return handleFetchWatchManage;
        case 'fetchCarrierPlans':
          return handleFetchCarrierPlans;
        case 'fetchWatchTransfer':
          return handleFetchWatchTransfer;
        default:
          return handleUndefinedFunction();
      }
    },
  });

export const getDefaultProxyFunction = (): unknown =>
  new Proxy<ProxyFunction>({} as ProxyFunction, {
    get(target, prop: string | symbol) {
      if (typeof prop === 'symbol' || prop in target) {
        return Reflect.get(target, prop);
      }

      console.log(`Calling undefined function ${prop}`);
      return handleUndefinedFunction();
    },
  });
